import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { CheckCircleOutlined, HighlightOffOutlined } from "@material-ui/icons";

// T-Marketing-225 / C-Marketing-237 (Q-Marketing-62 O-1,
// https://stage.uclusion.com/dd56682c-9920-417b-be46-7a30d41bc905/Q-Marketing-62):
// the dark contrast band that held "The Catch" now qualifies the visitor
// instead. A for/not-for matrix names the exact audience — people who code or
// research with AI and want a better way to track what they are doing — and
// pairs each yes with its no so a visitor can self-select in one scan.

const INK = '#1f2933';

const Band = styled.section`
  ${tw`w-full text-center`}
  background: ${INK};
  padding: 4rem 1.5rem 5rem;
`;

const Eyebrow = tw.div`font-sans text-sm font-bold tracking-widest uppercase text-blue-300 mb-4`;

const Matrix = styled.div`
  ${tw`mx-auto mt-10 grid gap-3 text-left`}
  max-width: ${(p) => (p.$isMobile ? '24rem' : '58rem')};
  grid-template-columns: ${(p) => (p.$isMobile ? '1fr' : '1fr 1fr')};
`;

const ColumnHead = styled.div`
  ${tw`font-sans text-sm font-bold tracking-widest uppercase text-center pb-1`}
  color: ${(p) => (p.$for ? '#34d399' : '#f87171')};
`;

const Cell = styled.div`
  ${tw`flex items-start text-left rounded-lg px-4 py-3 font-sans`}
  gap: 0.75rem;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${(p) => (p.$for ? '#d1fae5' : '#e5e7eb')};
  background: ${(p) => (p.$for ? 'rgba(52, 211, 153, 0.08)' : 'rgba(248, 113, 113, 0.06)')};
  border: 1px solid ${(p) => (p.$for ? 'rgba(52, 211, 153, 0.35)' : 'rgba(248, 113, 113, 0.28)')};
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    margin-top: 2px;
    color: ${(p) => (p.$for ? '#34d399' : '#f87171')};
  }
`;

const BlogLink = styled.a`
  color: #93c5fd;
  text-decoration: underline;
  &:hover {
    color: #bfdbfe;
  }
`;

const ArticleCallout = styled.aside`
  ${tw`w-full mx-auto mt-10 max-w-3xl text-left font-sans`}
  border-left: 4px solid #60a5fa;
  padding: 0.25rem 0 0.25rem 1.25rem;
`;

const ArticleBody = tw.p`mt-2 mb-0 text-base leading-relaxed text-gray-300`;

const ArticleLink = styled(BlogLink)`
  ${tw`inline-flex items-center mt-3 font-semibold`}
  gap: 0.5rem;
  text-decoration: none;

  span {
    transition: transform 150ms ease;
  }

  &:hover {
    text-decoration: underline;
  }

  &:hover span,
  &:focus-visible span {
    transform: translateX(0.25rem);
  }

  &:focus-visible {
    outline: 2px solid #bfdbfe;
    outline-offset: 4px;
    border-radius: 2px;
  }
`;

const TYPED_COLLABORATION_URL = 'https://blog.uclusion.com/engineering/2026/08/03/typed-collaboration.html';
const CODING_AGENT_HARNESS_URL = 'https://blog.uclusion.com/engineering/2026/08/20/coding-agent-harness.html';

const ROWS = [
    {
        yes: "You code or research and want a better way to see backlog and guide AI work.",
        no: "You're shopping for a traditional project management tool.",
    },
    {
        yes: "You want other developers' or customers' opinions.",
        no: "You mainly need to track due dates your clients decided.",
    },
    {
        yes: "You want human-in-the-loop development.",
        no: "You vibe code without steering or want AI customer support or dev ops with human backup.",
    },
    {
        yes: (
            <>
                You want{' '}
                <BlogLink href={TYPED_COLLABORATION_URL} target="_blank" rel="noopener noreferrer">
                    typed collaboration
                </BlogLink>
                {' '}— structured so it's easy to asynchronously understand context quickly.
            </>
        ),
        no: "You're happy steering AI and humans through extended chat and meetings.",
    },
];

const AudienceFit = ({ isMobile }) => {
    return (
        <Band id="audience-fit">
            <Eyebrow>Who it's for</Eyebrow>
            <Matrix $isMobile={isMobile}>
                {!isMobile && <ColumnHead $for>Uclusion is for you if…</ColumnHead>}
                {!isMobile && <ColumnHead>Not for you if…</ColumnHead>}
                {ROWS.map((row, index) => (
                    <React.Fragment key={index}>
                        <Cell $for>
                            <CheckCircleOutlined />
                            <span>{row.yes}</span>
                        </Cell>
                        <Cell>
                            <HighlightOffOutlined />
                            <span>{row.no}</span>
                        </Cell>
                    </React.Fragment>
                ))}
            </Matrix>
            <ArticleCallout>
                <ArticleBody>
                    Uclusion modifies harness prompts to enable human-in-the-loop collaboration
                </ArticleBody>
                <ArticleLink
                    href={CODING_AGENT_HARNESS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Learn more about The Coding Agent Is Only Half the System"
                >
                    Learn more <span aria-hidden="true">→</span>
                </ArticleLink>
            </ArticleCallout>
        </Band>
    );
};

export default AudienceFit;
